import { useEffect, useRef, useState } from 'react'
import { supabase } from '../supabaseClient'

const ORANGE = '#FF5722'
const BG = '#0D0D0D'
const CARD = '#141414'
const BORDER = '#1f1f1f'

const AVATAR_COLORS = ['#E91E63', '#2196F3', '#9C27B0', '#FF9800', '#00BCD4']

function BottomIcon({ id, active }) {
  const color = active ? ORANGE : '#444'

  if (id === 'home') {
    return active ? (
      <svg width="24" height="24" viewBox="0 0 24 24"><path fill={color} d="M12.2 3.5c.3 2.8-.8 4.4-2 5.7-1.1 1.2-2.2 2.3-2.2 4.1a4.1 4.1 0 008.2 0c0-2.9-1.8-4.7-4-9.8z" /></svg>
    ) : (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12.2 3.5c.3 2.8-.8 4.4-2 5.7-1.1 1.2-2.2 2.3-2.2 4.1a4.1 4.1 0 008.2 0c0-2.9-1.8-4.7-4-9.8z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
    )
  }
  if (id === 'discover') {
    return active ? (
      <svg width="24" height="24" viewBox="0 0 24 24"><path fill={color} d="M7.2 4.5c.8 0 1.4.6 1.4 1.4v3.9l3.6 3.6 1.9-1.9-.9-.9a1.2 1.2 0 011.7-1.7l2.7 2.7a1.2 1.2 0 01-1.7 1.7l-.9-.9-1.9 1.9 3.6 3.6c.5.5.5 1.3 0 1.8l-.8.8-4.4-4.4-4.4 4.4-.8-.8a1.3 1.3 0 010-1.8l3.6-3.6-3.3-3.3V5.9c0-.8.6-1.4 1.4-1.4z" /></svg>
    ) : (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M8.2 4.5v5.8l3.8 3.8M5.9 4.5h4.6M6.8 7h2.8M13.2 9.6l4 4M16.8 8.4l1.8 1.8M15.6 10.8L9 17.4M8.6 19.8L19.8 8.6" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
    )
  }
  if (id === 'matches') {
    return active ? (
      <svg width="24" height="24" viewBox="0 0 24 24"><path fill={color} d="M6 5.5h12a3 3 0 013 3v6a3 3 0 01-3 3h-7.1L6.5 21v-3.5H6a3 3 0 01-3-3v-6a3 3 0 013-3z" /></svg>
    ) : (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M6 5.5h12a3 3 0 013 3v6a3 3 0 01-3 3h-7.1L6.5 21v-3.5H6a3 3 0 01-3-3v-6a3 3 0 013-3z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
    )
  }
  return active ? (
    <svg width="24" height="24" viewBox="0 0 24 24"><circle cx="12" cy="8" r="4" fill={color} /><path fill={color} d="M4 21a8 8 0 0116 0H4z" /></svg>
  ) : (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><circle cx="12" cy="7" r="4" stroke={color} strokeWidth="2" /></svg>
  )
}

export default function Matches({ session, onNavigate }) {
  const [matches, setMatches] = useState([])
  const [activeMatch, setActiveMatch] = useState(null)
  const [messages, setMessages] = useState([])
  const [lastMessageByMatch, setLastMessageByMatch] = useState({})
  const [newMessage, setNewMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [loading, setLoading] = useState(true)
  const messagesEndRef = useRef(null)

  useEffect(() => {
    fetchMatches()
  }, []) // eslint-disable-line

  useEffect(() => {
    if (activeMatch) {
      fetchMessages(activeMatch.id)
      return subscribeToMessages(activeMatch.id)
    }
  }, [activeMatch]) // eslint-disable-line

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  async function fetchMatches() {
    setLoading(true)

    const { data, error } = await supabase
      .from('matches')
      .select(`
        *,
        user_1:users!matches_user_1_id_fkey (id, full_name, area),
        user_2:users!matches_user_2_id_fkey (id, full_name, area)
      `)
      .or(`user_1_id.eq.${session.user.id},user_2_id.eq.${session.user.id}`)
      .eq('status', 'active')
      .order('matched_at', { ascending: false })

    if (!error && data) {
      setMatches(data)
      await fetchLastMessages(data)
    }
    setLoading(false)
  }

  async function fetchLastMessages(matchesData) {
    if (!matchesData?.length) {
      setLastMessageByMatch({})
      return
    }
    const matchIds = matchesData.map(match => match.id)
    const { data, error } = await supabase
      .from('messages')
      .select('id, match_id, content, created_at')
      .in('match_id', matchIds)
      .order('created_at', { ascending: false })

    if (error || !data) return
    const latestByMatch = {}
    for (const msg of data) {
      if (!latestByMatch[msg.match_id]) latestByMatch[msg.match_id] = msg.content
    }
    setLastMessageByMatch(latestByMatch)
  }

  async function fetchMessages(matchId) {
    const { data, error } = await supabase
      .from('messages')
      .select('*')
      .eq('match_id', matchId)
      .order('created_at', { ascending: true })

    if (!error && data) setMessages(data)
  }

  function subscribeToMessages(matchId) {
    const channel = supabase.channel(`messages:${matchId}`)
    channel.on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'messages',
      filter: `match_id=eq.${matchId}`
    }, (payload) => {
      setMessages(prev => [...prev, payload.new])
      setLastMessageByMatch(prev => ({ ...prev, [matchId]: payload.new.content || '' }))
    })
    channel.subscribe()
    return () => supabase.removeChannel(channel)
  }

  async function sendMessage() {
    if (!newMessage.trim() || !activeMatch) return
    setSending(true)
    const content = newMessage.trim()
    const { error } = await supabase.from('messages').insert({
      match_id: activeMatch.id,
      sender_id: session.user.id,
      content
    })
    if (!error) {
      setNewMessage('')
      setLastMessageByMatch(prev => ({ ...prev, [activeMatch.id]: content }))
    }
    setSending(false)
  }

  function getOtherUser(match) {
    if (match.user_1_id === session.user.id) return match.user_2
    return match.user_1
  }

  function getInitials(name) {
    if (!name) return '?'
    return name
      .split(' ')
      .filter(Boolean)
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: BG,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#555',
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 14
      }}>
        Loading matches...
      </div>
    )
  }

  if (!activeMatch && matches.length === 0) {
    return (
      <div style={{
        minHeight: '100vh',
        background: BG,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 28,
        textAlign: 'center',
        gap: 14,
        fontFamily: "'DM Sans', sans-serif",
        paddingBottom: 92
      }}>
        <div style={{ fontSize: 52 }}>💫</div>
        <div style={{ color: '#fff', fontSize: 22, fontWeight: 800 }}>No active matches</div>
        <div style={{ color: '#666', fontSize: 14, lineHeight: 1.6, maxWidth: 320 }}>
          Once you match with someone, they will show up here.
          Post a craving and start swiping.
        </div>
        <button
          onClick={() => onNavigate('post')}
          style={{
            marginTop: 8,
            background: ORANGE,
            color: '#fff',
            border: 'none',
            borderRadius: 12,
            padding: '13px 24px',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: "'DM Sans', sans-serif"
          }}
        >
          Post a craving →
        </button>
        <div style={{ position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: '100%', background: '#0a0a0a', borderTop: `1.5px solid ${BORDER}`, display: 'flex', padding: '10px 0 20px', zIndex: 100 }}>
          {[
            { label: 'Home', id: 'home' },
            { label: 'Discover', id: 'discover' },
            { label: 'Matches', id: 'matches' },
            { label: 'Profile', id: 'profile' },
          ].map(nav => (
            <div key={nav.id} onClick={() => onNavigate(nav.id)} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: 'pointer', padding: '4px 0' }}>
              <BottomIcon id={nav.id} active={nav.id === 'matches'} />
              <span style={{ fontSize: 10, fontWeight: 500, color: nav.id === 'matches' ? ORANGE : '#444', letterSpacing: 0.3 }}>{nav.label}</span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (activeMatch) {
    return (
      <div style={{ minHeight: '100vh', background: BG, fontFamily: "'DM Sans', sans-serif", display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '52px 20px 16px', borderBottom: `1.5px solid ${BORDER}`, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            onClick={() => setActiveMatch(null)}
            style={{ width: 36, height: 36, borderRadius: '50%', background: CARD, border: `1.5px solid ${BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', fontSize: 16, flexShrink: 0 }}
          >
            ←
          </div>
          <div style={{ width: 42, height: 42, borderRadius: '50%', background: `${AVATAR_COLORS[0]}22`, border: `2px solid ${AVATAR_COLORS[0]}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: AVATAR_COLORS[0], fontWeight: 700, fontSize: 14, flexShrink: 0 }}>
            {getInitials(getOtherUser(activeMatch)?.full_name)}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>
              {getOtherUser(activeMatch)?.full_name || 'Your match'}
            </div>
            <div style={{ fontSize: 11, color: '#4CAF50' }}>🍳 You just got Sauced</div>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 20px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {messages.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 20px', color: '#444', fontSize: 13 }}>
              No messages yet. Say something. 👋
            </div>
          ) : (
            messages.map((msg) => {
              const isMe = msg.sender_id === session.user.id
              return (
                <div key={msg.id} style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                  <div style={{ maxWidth: '75%', background: isMe ? ORANGE : CARD, color: '#fff', borderRadius: isMe ? '18px 18px 4px 18px' : '18px 18px 18px 4px', padding: '10px 14px', fontSize: 14, lineHeight: 1.5, border: isMe ? 'none' : `1.5px solid ${BORDER}` }}>
                    {msg.content}
                  </div>
                </div>
              )
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        <div style={{ padding: '12px 20px 24px', borderTop: `1.5px solid ${BORDER}`, display: 'flex', gap: 10, background: BG }}>
          <input
            type="text"
            placeholder="Say something..."
            value={newMessage}
            onChange={e => setNewMessage(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && sendMessage()}
            style={{ flex: 1, background: CARD, border: `1.5px solid ${BORDER}`, borderRadius: 24, color: '#fff', padding: '12px 18px', fontSize: 14, outline: 'none', fontFamily: "'DM Sans', sans-serif" }}
          />
          <button
            onClick={sendMessage}
            disabled={!newMessage.trim() || sending}
            style={{ width: 48, height: 48, borderRadius: '50%', background: newMessage.trim() ? ORANGE : CARD, border: 'none', color: '#fff', fontSize: 20, cursor: newMessage.trim() ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
          >
            ↑
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: BG,
      fontFamily: "'DM Sans', sans-serif",
      color: '#fff',
      paddingBottom: 92
    }}>
      <div style={{
        padding: '52px 20px 18px',
        borderBottom: `1.5px solid ${BORDER}`,
        display: 'flex'
      }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 800 }}>Your matches</div>
          <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
            {matches.length} active {matches.length === 1 ? 'match' : 'matches'}
          </div>
        </div>
      </div>

      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {matches.map((match, i) => {
          const otherUser = getOtherUser(match)
          const name = otherUser?.full_name || 'Unknown user'
          const area = otherUser?.area || 'Bangalore'
          const color = AVATAR_COLORS[i % AVATAR_COLORS.length]
          const preview = lastMessageByMatch[match.id] || 'Say something 👋'

          return (
            <div
              key={match.id}
              onClick={() => setActiveMatch(match)}
              style={{
                background: CARD,
                border: `1.5px solid ${BORDER}`,
                borderRadius: 16,
                padding: 14,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                cursor: 'pointer'
              }}
            >
              <div style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: `${color}22`,
                border: `2px solid ${color}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: color,
                fontWeight: 800,
                fontSize: 14,
                flexShrink: 0
              }}>
                {getInitials(name)}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>
                  {name}
                </div>
                <div style={{ fontSize: 12, color: '#666', marginTop: 3 }}>
                  📍 {area}
                </div>
                <div style={{ fontSize: 12, color: '#8a8a8a', marginTop: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {preview}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div style={{
        position: 'fixed',
        bottom: 0, left: '50%',
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: '100%',
        background: '#0a0a0a',
        borderTop: `1.5px solid ${BORDER}`,
        display: 'flex',
        padding: '10px 0 20px',
        zIndex: 100
      }}>
        {[
          { label: 'Home', id: 'home' },
          { label: 'Discover', id: 'discover' },
          { label: 'Matches', id: 'matches' },
          { label: 'Profile', id: 'profile' },
        ].map(nav => (
          <div
            key={nav.id}
            onClick={() => onNavigate(nav.id)}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 4,
              cursor: 'pointer',
              padding: '4px 0'
            }}
          >
            <BottomIcon id={nav.id} active={nav.id === 'matches'} />
            <span style={{
              fontSize: 10,
              fontWeight: 500,
              color: nav.id === 'matches' ? ORANGE : '#444',
              letterSpacing: 0.3
            }}>
              {nav.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
